import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Trash2, RefreshCw, AlertTriangle, Search, Users, UserCog,
  CreditCard, Wallet, UserSearch, MessageSquare, MessageCircle,
  Package, Dumbbell, ChevronLeft, ChevronRight, RotateCcw,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";

// ─── Tab Config ────────────────────────────────────────────────────────────────

const TABS = [
  { key: "members",          label: "Members",           icon: Users },
  { key: "staff",            label: "Staff",             icon: UserCog },
  { key: "memberships",      label: "Memberships",       icon: CreditCard },
  { key: "payments",         label: "Payments",          icon: Wallet },
  { key: "leads",            label: "Leads",             icon: UserSearch },
  { key: "lead_followups",   label: "Lead Followups",    icon: MessageCircle },
  { key: "member_followups", label: "Member Followups",  icon: MessageSquare },
  { key: "packages",         label: "Packages",          icon: Package },
  { key: "workouts",         label: "Workouts",          icon: Dumbbell },
];

// ─── Table Column Definitions ──────────────────────────────────────────────────

function getColumns(tab) {
  switch (tab) {
    case "members":
      return [
        { key: "name",         label: "Name" },
        { key: "phone",        label: "Phone" },
        { key: "email",        label: "Email" },
        { key: "joiningDate",  label: "Joined" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "staff":
      return [
        { key: "name",         label: "Name" },
        { key: "email",        label: "Email" },
        { key: "phone",        label: "Phone" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "memberships":
      return [
        { key: "memberName",   label: "Member" },
        { key: "memberPhone",  label: "Phone" },
        { key: "packageName",  label: "Package" },
        { key: "startDate",    label: "Start" },
        { key: "endDate",      label: "End" },
        { key: "finalAmount",  label: "Amount", isCurrency: true },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "payments":
      return [
        { key: "memberName",   label: "Member" },
        { key: "memberPhone",  label: "Phone" },
        { key: "amount",       label: "Amount", isCurrency: true },
        { key: "paymentMode",  label: "Mode" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "leads":
      return [
        { key: "name",         label: "Name" },
        { key: "phone",        label: "Phone" },
        { key: "source",       label: "Source" },
        { key: "status",       label: "Status" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "lead_followups":
      return [
        { key: "leadName",     label: "Lead" },
        { key: "leadPhone",    label: "Phone" },
        { key: "scheduledDate",label: "Scheduled" },
        { key: "status",       label: "Status" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "member_followups":
      return [
        { key: "memberName",   label: "Member" },
        { key: "title",        label: "Title" },
        { key: "scheduledDate",label: "Scheduled" },
        { key: "status",       label: "Status" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "packages":
      return [
        { key: "name",         label: "Name" },
        { key: "price",        label: "Price", isCurrency: true },
        { key: "durationMonths",label: "Duration (mo)" },
        { key: "status",       label: "Status" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    case "workouts":
      return [
        { key: "title",        label: "Title" },
        { key: "deletedByName",label: "Deleted By" },
        { key: "deletedAt",    label: "Deleted At", isDate: true },
      ];
    default:
      return [];
  }
}

// ─── Helpers ───────────────────────────────────────────────────────────────────

function fmt(val, col) {
  if (!val && val !== 0) return <span className="text-muted-foreground/50">—</span>;
  if (col.isDate) {
    try {
      return new Date(val).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
    } catch { return val; }
  }
  if (col.isCurrency) return `₹${Number(val).toLocaleString("en-IN")}`;
  return String(val);
}

// ─── Confirm Dialog ────────────────────────────────────────────────────────────

function ConfirmDialog({ open, onClose, onConfirm, title, description, danger }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-2xl shadow-2xl p-6 w-full max-w-sm mx-4 animate-fade-in-up">
        <div className="flex items-start gap-4">
          <div className={`flex-shrink-0 w-10 h-10 rounded-xl flex items-center justify-center ${danger ? "bg-destructive/10" : "bg-primary/10"}`}>
            <AlertTriangle className={`h-5 w-5 ${danger ? "text-destructive" : "text-primary"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm mb-1">{title}</h3>
            <p className="text-xs text-muted-foreground">{description}</p>
          </div>
        </div>
        <div className="flex gap-2 mt-5 justify-end">
          <Button variant="outline" size="sm" onClick={onClose} className="rounded-lg">Cancel</Button>
          <Button
            size="sm"
            onClick={onConfirm}
            className={`rounded-lg ${danger ? "bg-destructive hover:bg-destructive/90 text-white" : ""}`}
          >
            Confirm
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

export default function DeletedItems() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("members");
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null); // { type: "restore"|"purge", id }
  const [actionLoading, setActionLoading] = useState(null);
  const LIMIT = 20;

  const fetchItems = useCallback(async () => {
    if (user?.role !== "owner") return;
    setLoading(true);
    try {
      const res = await api.get("/deleted-items", {
        params: { tab: activeTab, page, limit: LIMIT, search },
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      toast.error("Failed to load deleted items");
    } finally {
      setLoading(false);
    }
  }, [activeTab, page, search, user?.role]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const handleTabChange = (key) => {
    setActiveTab(key);
    setPage(1);
    setSearch("");
    setSearchInput("");
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleRestore = async (id) => {
    setActionLoading(id + "_restore");
    try {
      await api.post("/deleted-items/restore", { tab: activeTab, id });
      toast.success("Item restored successfully");
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to restore item");
    } finally {
      setActionLoading(null);
      setConfirm(null);
    }
  };

  const handlePurge = async (id) => {
    setActionLoading(id + "_purge");
    try {
      await api.delete("/deleted-items/purge", { data: { tab: activeTab, id } });
      toast.success("Item permanently deleted");
      fetchItems();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to permanently delete item");
    } finally {
      setActionLoading(null);
      setConfirm(null);
    }
  };

  // Owner guard
  if (user?.role !== "owner") {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4 text-center">
        <div className="w-16 h-16 rounded-2xl bg-destructive/10 flex items-center justify-center">
          <Trash2 className="h-8 w-8 text-destructive" />
        </div>
        <h2 className="text-xl font-semibold">Access Denied</h2>
        <p className="text-muted-foreground text-sm">Only gym owners can access deleted items.</p>
      </div>
    );
  }

  const totalPages = Math.ceil(total / LIMIT);
  const columns = getColumns(activeTab);
  const activeTabConfig = TABS.find((t) => t.key === activeTab);

  return (
    <div className="flex flex-col gap-6 pb-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <div className="w-9 h-9 rounded-xl bg-destructive/10 flex items-center justify-center">
              <Trash2 className="h-4 w-4 text-destructive" />
            </div>
            <h1 className="text-2xl font-bold font-heading">Deleted Items</h1>
          </div>
          <p className="text-sm text-muted-foreground ml-12">
            View and manage all soft-deleted records. Restore or permanently remove them.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchItems} className="gap-2 rounded-xl">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 scrollbar-none">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              id={`deleted-tab-${tab.key}`}
              onClick={() => handleTabChange(tab.key)}
              className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-medium whitespace-nowrap transition-all duration-200 border
                ${isActive
                  ? "bg-destructive text-white border-destructive shadow-sm"
                  : "bg-card text-muted-foreground border-border hover:text-foreground hover:bg-secondary"
                }`}
            >
              <Icon className="h-3.5 w-3.5 shrink-0" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Search + Stats Bar */}
      <div className="flex items-center gap-3 flex-wrap">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px] max-w-sm">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              id="deleted-items-search"
              placeholder={`Search deleted ${activeTabConfig?.label?.toLowerCase()}...`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9 h-9 text-sm rounded-xl"
            />
          </div>
          <Button type="submit" size="sm" variant="secondary" className="rounded-xl h-9 px-4">
            Search
          </Button>
        </form>
        {total > 0 && (
          <Badge variant="secondary" className="rounded-full text-xs font-mono">
            {total} deleted {activeTabConfig?.label?.toLowerCase()}
          </Badge>
        )}
      </div>

      {/* Table */}
      <div className="bg-card border border-border rounded-2xl overflow-hidden shadow-sm">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-destructive" />
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-48 gap-3">
            <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center">
              <Trash2 className="h-6 w-6 text-muted-foreground" />
            </div>
            <div className="text-center">
              <p className="font-medium text-sm">No deleted {activeTabConfig?.label?.toLowerCase()}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {search ? "Try a different search term." : "Nothing has been soft-deleted here yet."}
              </p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border bg-secondary/30">
                  {columns.map((col) => (
                    <th key={col.key} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                  <th className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((item, idx) => (
                  <tr
                    key={item.id || idx}
                    className="border-b border-border/50 hover:bg-secondary/20 transition-colors"
                  >
                    {columns.map((col) => (
                      <td key={col.key} className="px-4 py-3 text-sm whitespace-nowrap">
                        {fmt(item[col.key], col)}
                      </td>
                    ))}
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {/* Restore */}
                        <Button
                          id={`restore-${item.id}`}
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 text-xs rounded-lg border-emerald-500/50 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
                          onClick={() => setConfirm({ type: "restore", id: item.id })}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === item.id + "_restore" ? (
                            <div className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <RotateCcw className="h-3 w-3" />
                          )}
                          Restore
                        </Button>
                        {/* Permanent Delete */}
                        <Button
                          id={`purge-${item.id}`}
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1.5 text-xs rounded-lg border-destructive/50 text-destructive hover:bg-destructive/10"
                          onClick={() => setConfirm({ type: "purge", id: item.id })}
                          disabled={!!actionLoading}
                        >
                          {actionLoading === item.id + "_purge" ? (
                            <div className="h-3 w-3 border border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <Trash2 className="h-3 w-3" />
                          )}
                          Delete Forever
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Showing {(page - 1) * LIMIT + 1}–{Math.min(page * LIMIT, total)} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-lg"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="font-mono text-xs px-2">
              {page} / {totalPages}
            </span>
            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 rounded-lg"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={!!confirm}
        onClose={() => setConfirm(null)}
        danger={confirm?.type === "purge"}
        title={
          confirm?.type === "restore"
            ? "Restore this item?"
            : "Permanently delete this item?"
        }
        description={
          confirm?.type === "restore"
            ? "This item will be restored and appear in its original list again."
            : "This action cannot be undone. The item and all associated records will be permanently removed from the database."
        }
        onConfirm={() => {
          if (confirm?.type === "restore") handleRestore(confirm.id);
          else handlePurge(confirm.id);
        }}
      />
    </div>
  );
}
